import { useEffect, useRef, useState } from 'react';
import { prefersReducedMotion } from '../hooks/useReducedMotion';

interface CharsProps {
  text: string;
  className?: string;
  delay?: number;
  step?: number;
}

export function Chars({ text, className = '', delay = 0, step = 22 }: CharsProps) {
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (prefersReducedMotion() || typeof IntersectionObserver === 'undefined') {
      node.dataset.shown = 'true';
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          (entry.target as HTMLElement).dataset.shown = 'true';
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.25, rootMargin: '0px 0px -8% 0px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const words = text.split(' ');
  let index = 0;

  return (
    <span ref={ref} className={`v5-chars ${className}`.trim()} aria-label={text}>
      {words.map((word, w) => (
        <span className="v5-chars-word" key={`${word}-${w}`} aria-hidden="true">
          {Array.from(word).map((ch, c) => {
            const d = delay + index * step;
            index += 1;
            return (
              <span className="v5-char" key={`${ch}-${c}`} style={{ transitionDelay: `${d}ms` }}>
                {ch}
              </span>
            );
          })}
          {w < words.length - 1 ? <span className="v5-char-space"> </span> : null}
        </span>
      ))}
    </span>
  );
}

const GLYPHS = '0123456789ABCDEFXZ$#%/\\|<>+-';

export function useScramble(value: string, active: boolean, duration = 620) {
  const [display, setDisplay] = useState(active ? value : value);
  const frame = useRef(0);

  useEffect(() => {
    if (!active) return;
    if (prefersReducedMotion()) {
      setDisplay(value);
      return;
    }
    const chars = Array.from(value);
    const start = performance.now();

    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const settled = Math.floor(t * chars.length);
      let out = '';
      for (let i = 0; i < chars.length; i += 1) {
        const ch = chars[i] ?? '';
        if (i < settled || ch === ' ' || ch === '/' || ch === '+') out += ch;
        else out += GLYPHS[Math.floor(Math.random() * GLYPHS.length)] ?? ch;
      }
      setDisplay(out);
      if (t < 1) frame.current = requestAnimationFrame(tick);
      else {
        setDisplay(value);
        frame.current = 0;
      }
    };

    frame.current = requestAnimationFrame(tick);
    return () => {
      if (frame.current) cancelAnimationFrame(frame.current);
      frame.current = 0;
    };
  }, [value, active, duration]);

  return display;
}

export function useMagnetic<T extends HTMLElement>(strength = 0.28) {
  const ref = useRef<T>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (prefersReducedMotion()) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    let raf = 0;
    let tx = 0;
    let ty = 0;

    const apply = () => {
      raf = 0;
      node.style.transform = `translate3d(${tx.toFixed(2)}px, ${ty.toFixed(2)}px, 0)`;
    };

    const onMove = (event: PointerEvent) => {
      const rect = node.getBoundingClientRect();
      const dx = event.clientX - (rect.left + rect.width / 2);
      const dy = event.clientY - (rect.top + rect.height / 2);
      tx = dx * strength;
      ty = dy * strength;
      if (!raf) raf = requestAnimationFrame(apply);
    };

    const onLeave = () => {
      tx = 0;
      ty = 0;
      if (!raf) raf = requestAnimationFrame(apply);
    };

    node.addEventListener('pointermove', onMove);
    node.addEventListener('pointerleave', onLeave);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      node.removeEventListener('pointermove', onMove);
      node.removeEventListener('pointerleave', onLeave);
      node.style.transform = '';
    };
  }, [strength]);

  return ref;
}

export function useInView<T extends HTMLElement>(threshold = 0.3) {
  const ref = useRef<T>(null);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof IntersectionObserver === 'undefined') {
      setSeen(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          setSeen(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold, rootMargin: '0px 0px -8% 0px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, seen] as const;
}
